import { useCallback, useEffect, useState } from 'react';
import selectorService from '@/core/services/SelectorService';

export interface SelectorItems {
  groups: string[];
  themes: string[];
  tags: string[];
  occupations: string[];
}

export interface UseSelectorItemsResult extends SelectorItems {
  loading: boolean;
  addTagsLocally: (newTags: string[]) => void;
  refetchTags: () => Promise<void>;
}

const EMPTY_ITEMS: SelectorItems = { groups: [], themes: [], tags: [], occupations: [] };

// Module-level cache: shared across all hook consumers.
// The fetch runs exactly once per browser session; subsequent hook calls
// receive the cached result synchronously (or the in-flight promise).
let cachedItems: SelectorItems | null = null;
let fetchPromise: Promise<SelectorItems> | null = null;

function startFetch(): Promise<SelectorItems> {
  if (fetchPromise !== null) return fetchPromise;
  fetchPromise = Promise.allSettled([
    selectorService.getAllGroupsPaginated(),
    selectorService.getAllThemesPaginated(),
    selectorService.getAllTagsPaginated(),
    selectorService.getAllOccupationsPaginated(),
  ]).then(([groups, themes, tags, occupations]) => {
    const notClassified = 'não classificados';
    const isNotClassified = (item: string) => item.toLowerCase() === notClassified;
    const result: SelectorItems = {
      groups: groups.status === 'fulfilled' ? groups.value.groups.filter((g) => !isNotClassified(g)).sort((a, b) => a.localeCompare(b, 'pt-BR')) : [],
      themes: themes.status === 'fulfilled' ? themes.value.themes.filter((t) => !isNotClassified(t)).sort((a, b) => a.localeCompare(b, 'pt-BR')) : [],
      tags: tags.status === 'fulfilled' ? tags.value.tags : [],
      occupations: occupations.status === 'fulfilled' ? occupations.value.occupations : [],
    };
    
    // Check if any request failed
    const hasFailure = [groups, themes, tags, occupations].some((r) => r.status === 'rejected');
    
    if (hasFailure) {
      // Do not cache partial results - allow retry on next mount
      fetchPromise = null;
    } else {
      // Only cache if all requests succeeded
      cachedItems = result;
    }
    
    return result;
  });
  return fetchPromise;
}

/**
 * Triggers the selector items fetch eagerly without subscribing to the result.
 * Call this at the top of components that always mount on pages with the filter
 * (e.g. GlobalFilterApp, Header) so data is ready before the user opens a section.
 */
export function prefetchSelectorItems(): void {
  startFetch();
}

/**
 * Resets the module-level cache.
 *
 * This function exists in the production file because `cachedItems` and
 * `fetchPromise` are module-scoped variables that cannot be accessed from
 * outside the module. `jest.resetModules()` is not a viable alternative here
 * because it would reload React, causing a "multiple React instances" conflict
 * with `@testing-library/react`'s `renderHook`.
 *
 * The `_` prefix and the `NODE_ENV` guard signal that this is exclusively for
 * unit tests and is stripped from production builds.
 *
 * @internal
 */
export function _resetCacheForTesting(): void {
  if (process.env.NODE_ENV === 'production') return;
  cachedItems = null;
  fetchPromise = null;
}

export function useSelectorItems(): UseSelectorItemsResult {
  const [items, setItems] = useState<SelectorItems>(() => cachedItems ?? EMPTY_ITEMS);
  const [loading, setLoading] = useState<boolean>(!cachedItems);

  useEffect(() => {
    if (cachedItems) return; // state already initialized correctly via useState initializer
    let cancelled = false;
    startFetch()
      .then((result) => {
        if (!cancelled) {
          setItems(result);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          // Set loading to false on error so UI doesn't hang
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const addTagsLocally = useCallback((newTags: string[]) => {
    setItems((prev) => {
      const updated = { ...prev, tags: [...new Set([...prev.tags, ...newTags])] };
      // Schedule cache update after React commits the state
      Promise.resolve().then(() => {
        cachedItems = updated;
      });
      return updated;
    });
  }, []);

  const refetchTags = useCallback(async () => {
    try {
      const result = await selectorService.getAllTagsPaginated();
      setItems((prev) => {
        const updated = { ...prev, tags: result.tags };
        // Schedule cache update after React commits the state
        Promise.resolve().then(() => {
          cachedItems = updated;
        });
        return updated;
      });
    } catch {
      // keep existing tags on error
    }
  }, []);

  return { ...items, loading, addTagsLocally, refetchTags };
}
